import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchAdminPurchasing, type AdminPurchasingResponse } from "../../lib/admin-purchasing";
import { formatCurrency } from "../../lib/utils";

const emptyData: AdminPurchasingResponse = {
  filters: {
    query: "",
    threshold: 10,
    supplierId: null
  },
  summary: {
    lowStockProducts: 0,
    mappedProducts: 0,
    sourcingGaps: 0,
    supplierLines: 0,
    estimatedTotal: 0
  },
  suppliers: [],
  candidates: [],
  supplierOrder: [],
  sourcingGaps: []
};

function escapeCsvCell(value: string | number) {
  const text = String(value);
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }

  return text;
}

export function AdminPurchasingPage() {
  const [data, setData] = useState<AdminPurchasingResponse>(emptyData);
  const [query, setQuery] = useState("");
  const [threshold, setThreshold] = useState("10");
  const [supplierId, setSupplierId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        if (query.trim()) {
          params.set("q", query.trim());
        }
        params.set("threshold", threshold || "10");
        if (supplierId) {
          params.set("supplierId", supplierId);
        }

        const result = await fetchAdminPurchasing(params);
        if (!ignore) {
          setData(result);
        }
      } catch (loadError) {
        if (!ignore) {
          setError(loadError instanceof Error ? loadError.message : "Unable to load purchasing data.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void load();

    return () => {
      ignore = true;
    };
  }, [query, threshold, supplierId]);

  const metrics = useMemo(
    () => [
      { label: "Low stock", value: data.summary.lowStockProducts },
      { label: "Mapped", value: data.summary.mappedProducts },
      { label: "Gaps", value: data.summary.sourcingGaps },
      { label: "Estimate", value: formatCurrency(data.summary.estimatedTotal) }
    ],
    [data.summary]
  );

  function downloadLowStockCsv() {
    if (data.candidates.length === 0) {
      return;
    }

    const rows = [
      ["Product ID", "Product", "Category", "Current Stock", "Supplier Links", "Sale Price"],
      ...data.candidates.map((item) => [item.id, item.name, item.category, item.quantity, item.supplierLinks, item.price])
    ];

    const csv = rows.map((row) => row.map(escapeCsvCell).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const safeQuery = query.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    const filenameParts = ["low-stock", `threshold-${threshold || "10"}`];

    if (safeQuery) {
      filenameParts.push(safeQuery);
    }

    link.href = url;
    link.download = `${filenameParts.join("_")}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  function downloadSupplierOrderCsv() {
    if (data.supplierOrder.length === 0) {
      return;
    }

    const selectedSupplier = data.suppliers.find((supplier) => String(supplier.id) === supplierId);
    const rows = [
      ["SKU", "Inventory Product", "Supplier Product", "Current Stock", "Order Quantity", "Unit Price", "Total"],
      ...data.supplierOrder.map((line) => [
        line.sku,
        line.inventoryName,
        line.supplierProductName,
        line.currentStock,
        line.orderQuantity,
        line.unitPrice,
        line.total
      ])
    ];

    const csv = rows.map((row) => row.map(escapeCsvCell).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const supplierSlug = (selectedSupplier?.name || "supplier")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    const safeQuery = query.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    const filenameParts = ["supplier-order", supplierSlug || "supplier", `threshold-${threshold || "10"}`];

    if (safeQuery) {
      filenameParts.push(safeQuery);
    }

    link.href = url;
    link.download = `${filenameParts.join("_")}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6 pb-6">
      <div>
        <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Purchasing</p>
        <h1 className="mt-3 text-4xl font-semibold tracking-tight">Reorder and procurement planning</h1>
        <p className="mt-4 max-w-3xl text-nv-slate">Filter low-stock inventory, generate supplier-specific reorder lines, and expose sourcing gaps before procurement starts.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {metrics.map((metric) => (
          <div className="admin-card rounded-[28px] p-5" key={metric.label}>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">{metric.label}</p>
            <p className="mt-3 break-words text-3xl font-semibold tracking-tight text-nv-ink">{metric.value}</p>
          </div>
        ))}
      </div>

      <div className="admin-card rounded-[32px] p-5">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_160px_240px]">
          <input
            className="field-control px-4 py-3 text-sm"
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search low-stock items by product, SKU, slug, or category"
            value={query}
          />
          <input
            className="field-control px-4 py-3 text-sm"
            min="0"
            onChange={(event) => setThreshold(event.target.value)}
            type="number"
            value={threshold}
          />
          <select className="field-control px-4 py-3 text-sm" onChange={(event) => setSupplierId(event.target.value)} value={supplierId}>
            <option value="">All suppliers / sourcing view</option>
            {data.suppliers.map((supplier) => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error ? <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-300">{error}</div> : null}

      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
        <div className="min-w-0 space-y-4">
          <div className="admin-card overflow-hidden rounded-[32px]">
            <div className="flex items-center justify-between gap-3 px-5 py-5">
              <div>
                <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Low stock pool</p>
                <h2 className="mt-2 text-2xl font-semibold tracking-tight text-nv-ink">Products below threshold</h2>
              </div>
              <button
                className="rounded-full border border-black/10 px-4 py-2 text-sm font-semibold disabled:opacity-40 dark:border-white/10"
                disabled={loading || data.candidates.length === 0}
                onClick={downloadLowStockCsv}
                type="button"
              >
                Download CSV
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px]">
                <thead>
                  <tr className="border-b border-black/10 text-left text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">
                    <th className="px-5 py-4">Product</th>
                    <th className="px-5 py-4">Category</th>
                    <th className="px-5 py-4">Current stock</th>
                    <th className="px-5 py-4">Suppliers</th>
                    <th className="px-5 py-4">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td className="px-5 py-8 text-sm text-nv-slate" colSpan={5}>
                        Loading purchasing candidates...
                      </td>
                    </tr>
                  ) : data.candidates.length > 0 ? (
                    data.candidates.map((item) => (
                      <tr className="border-b border-black/5 align-top last:border-b-0" key={item.id}>
                        <td className="px-5 py-4">
                          <Link className="font-semibold text-nv-ink transition hover:text-nv-electric" to={`/shop/product/${item.slug}/${item.id}`}>
                            {item.name}
                          </Link>
                        </td>
                        <td className="px-5 py-4 text-sm text-nv-slate">{item.category}</td>
                        <td className="px-5 py-4">
                          <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${item.quantity <= 2 ? "border-red-200 bg-red-50 text-red-700 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-300" : "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/70 dark:bg-amber-950/40 dark:text-amber-300"}`}>
                            {item.quantity}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-sm text-nv-slate">{item.supplierLinks}</td>
                        <td className="px-5 py-4 text-sm text-nv-slate">{formatCurrency(item.price)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="px-5 py-8 text-sm text-nv-slate" colSpan={5}>
                        No products match the current purchasing filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="admin-card overflow-hidden rounded-[32px]">
            <div className="flex items-center justify-between gap-3 px-5 py-5">
              <div>
                <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Supplier order</p>
                <h2 className="mt-2 text-2xl font-semibold tracking-tight text-nv-ink">
                  {supplierId ? "Supplier-ready reorder lines" : "Select a supplier to generate an order list"}
                </h2>
              </div>
              <div className="flex items-center gap-3">
                {supplierId ? <p className="text-sm font-semibold text-nv-slate">{data.summary.supplierLines} lines</p> : null}
                <button
                  className="rounded-full border border-black/10 px-4 py-2 text-sm font-semibold disabled:opacity-40 dark:border-white/10"
                  disabled={loading || !supplierId || data.supplierOrder.length === 0}
                  onClick={downloadSupplierOrderCsv}
                  type="button"
                >
                  Download CSV
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px]">
                <thead>
                  <tr className="border-b border-black/10 text-left text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">
                    <th className="px-5 py-4">SKU</th>
                    <th className="px-5 py-4">Product</th>
                    <th className="px-5 py-4">Current</th>
                    <th className="px-5 py-4">Order qty</th>
                    <th className="px-5 py-4">Unit price</th>
                    <th className="px-5 py-4">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td className="px-5 py-8 text-sm text-nv-slate" colSpan={6}>
                        Loading supplier order list...
                      </td>
                    </tr>
                  ) : data.supplierOrder.length > 0 ? (
                    data.supplierOrder.map((line) => (
                      <tr className="border-b border-black/5 align-top last:border-b-0" key={line.id}>
                        <td className="px-5 py-4 text-sm text-nv-slate">{line.sku}</td>
                        <td className="px-5 py-4">
                          {line.inventoryId && line.inventorySlug ? (
                            <Link className="font-semibold text-nv-ink transition hover:text-nv-electric" to={`/shop/product/${line.inventorySlug}/${line.inventoryId}`}>
                              {line.inventoryName}
                            </Link>
                          ) : (
                            <p className="font-semibold text-nv-ink">{line.inventoryName}</p>
                          )}
                          <p className="mt-1 text-xs text-nv-slate">{line.supplierProductName}</p>
                        </td>
                        <td className="px-5 py-4 text-sm text-nv-slate">{line.currentStock}</td>
                        <td className="px-5 py-4 text-sm text-nv-slate">{line.orderQuantity}</td>
                        <td className="px-5 py-4 text-sm text-nv-slate">{formatCurrency(line.unitPrice)}</td>
                        <td className="px-5 py-4 font-semibold text-nv-ink">{formatCurrency(line.total)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="px-5 py-8 text-sm text-nv-slate" colSpan={6}>
                        {supplierId ? "No supplier mappings matched the current low-stock filter." : "Pick a supplier to build a reorder list."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="min-w-0 space-y-4">
          <div className="admin-card rounded-[32px] p-6">
            <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Sourcing gaps</p>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-nv-ink">
              {supplierId ? "Items missing for selected supplier" : "Items without supplier coverage"}
            </h2>
            <div className="mt-5 space-y-3">
              {loading ? (
                <div className="text-sm text-nv-slate">Loading sourcing gaps...</div>
              ) : data.sourcingGaps.length > 0 ? (
                data.sourcingGaps.map((item) => (
                  <div className="field-soft flex items-start justify-between gap-4 p-4" key={item.id}>
                    <div className="min-w-0">
                      <Link className="block truncate font-semibold text-nv-ink transition hover:text-nv-electric" to={`/shop/product/${item.slug}/${item.id}`}>
                        {item.name}
                      </Link>
                      <p className="mt-1 text-xs text-nv-slate">{item.category}</p>
                    </div>
                    <div className="text-right text-sm">
                      <p className="font-semibold text-nv-ink">Stock {item.quantity}</p>
                      <p className="mt-1 text-nv-slate">{item.supplierLinks} mappings</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">
                  No sourcing gaps for the current view.
                </div>
              )}
            </div>
          </div>

          <div className="admin-card rounded-[32px] p-6">
            <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Next action</p>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-nv-ink">Procurement workflow</h2>
            <div className="mt-4 space-y-3 text-sm text-nv-slate">
              <p>1. Set the stock threshold for the replenishment pass.</p>
              <p>2. Pick a supplier to generate mapped reorder lines.</p>
              <p>3. Resolve sourcing gaps in the supplier workspace before sending the purchase request.</p>
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link className="brand-button rounded-full px-5 py-3 text-sm font-semibold text-white" to="/admin/suppliers">
                Manage suppliers
              </Link>
              <Link className="rounded-full border border-black/10 px-5 py-3 text-sm font-semibold dark:border-white/10" to="/admin/catalog">
                Review catalog
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
